import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from './Icon';
import '../css/users-actions-menu.css';

// Меню «Действия» над списком пользователей: отчёт (ADMIN/DEV) и обычный экспорт.
// Для каждого действия выбирается аудитория — все по текущим фильтрам или только
// пользователи с согласием на рассылку (marketingConsent=true поверх фильтров).
const PANEL_TRANSITION = { duration: 0.16, ease: 'easeOut' };

const UsersActionsMenu = ({
  canGenerateReport = false,
  totalCount = 0,
  marketingOnlyFilter = false,
  onRequestMarketingCount,
  onExport,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [action, setAction] = useState(null);
  const [busyAudience, setBusyAudience] = useState(null);
  const [marketingCount, setMarketingCount] = useState(null);
  const [marketingCountLoading, setMarketingCountLoading] = useState(false);
  const wrapperRef = useRef(null);

  const close = () => {
    setIsOpen(false);
    setAction(null);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (busyAudience) return;
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) close();
    };
    const handleKey = (event) => {
      if (event.key !== 'Escape' || busyAudience) return;
      if (action) setAction(null);
      else close();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, action, busyAudience]);

  // Количество получателей рассылки подгружаем только при открытии выбора аудитории.
  useEffect(() => {
    if (!action) return undefined;
    if (marketingOnlyFilter) {
      setMarketingCount(totalCount);
      return undefined;
    }
    if (!onRequestMarketingCount) return undefined;
    let cancelled = false;
    setMarketingCount(null);
    setMarketingCountLoading(true);
    onRequestMarketingCount()
      .then((count) => {
        if (!cancelled) setMarketingCount(count);
      })
      .catch(() => {
        if (!cancelled) setMarketingCount(null);
      })
      .finally(() => {
        if (!cancelled) setMarketingCountLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [action, marketingOnlyFilter, totalCount, onRequestMarketingCount]);

  const handleSelectAudience = async (audience) => {
    if (busyAudience) return;
    setBusyAudience(audience);
    try {
      const done = await onExport(action, audience);
      if (done !== false) close();
    } finally {
      setBusyAudience(null);
    }
  };

  const actions = [
    canGenerateReport && {
      key: 'report',
      icon: 'file-text',
      label: t('users.actions.generateReport'),
      hint: t('users.actions.generateReportHint'),
      badge: t('users.actions.adminBadge'),
    },
    {
      key: 'export',
      icon: 'download',
      label: t('users.actions.exportData'),
      hint: t('users.actions.exportDataHint'),
    },
  ].filter(Boolean);

  const activeAction = actions.find((a) => a.key === action);

  const formatCount = (value, loadingValue) => {
    if (loadingValue) return <span className="users-actions-count-skeleton" aria-hidden />;
    if (value == null) return '—';
    return Number(value).toLocaleString();
  };

  const audiences = [
    {
      key: 'all',
      icon: 'users',
      label: t('users.actions.audienceAll'),
      hint: t('users.actions.audienceAllHint'),
      count: formatCount(totalCount, false),
    },
    {
      key: 'marketing',
      icon: 'mail',
      label: t('users.actions.audienceMarketing'),
      hint: t('users.actions.audienceMarketingHint'),
      count: formatCount(marketingCount, marketingCountLoading),
      accent: true,
    },
  ];

  return (
    <div className="users-actions" ref={wrapperRef}>
      <button
        type="button"
        className={`users-actions-trigger${isOpen ? ' is-open' : ''}`}
        onClick={() => (isOpen ? close() : setIsOpen(true))}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {busyAudience ? (
          <span className="users-actions-spinner" aria-hidden />
        ) : (
          <Icon name="settings" size={16} />
        )}
        <span>{busyAudience ? t('users.actions.preparing') : t('users.actions.button')}</span>
        <Icon name="chevron-down" size={14} className="users-actions-trigger-chevron" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="users-actions-dropdown"
            role="menu"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={PANEL_TRANSITION}
          >
            <AnimatePresence mode="wait" initial={false}>
              {!activeAction ? (
                <motion.div
                  key="actions"
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={PANEL_TRANSITION}
                >
                  {actions.map((item, index) => (
                    <React.Fragment key={item.key}>
                      {index > 0 && <div className="users-actions-divider" />}
                      <button
                        type="button"
                        role="menuitem"
                        className="users-actions-item"
                        onClick={() => setAction(item.key)}
                      >
                        <span className="users-actions-item-icon">
                          <Icon name={item.icon} size={16} />
                        </span>
                        <span className="users-actions-item-text">
                          <span className="users-actions-item-label">
                            {item.label}
                            {item.badge && <span className="users-actions-badge">{item.badge}</span>}
                          </span>
                          <span className="users-actions-item-hint">{item.hint}</span>
                        </span>
                        <Icon name="chevron-right" size={14} className="users-actions-item-chevron" />
                      </button>
                    </React.Fragment>
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  key="audience"
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 12 }}
                  transition={PANEL_TRANSITION}
                >
                  <div className="users-actions-panel-header">
                    <button
                      type="button"
                      className="users-actions-back"
                      onClick={() => setAction(null)}
                      disabled={Boolean(busyAudience)}
                      aria-label={t('users.actions.back')}
                    >
                      <Icon name="chevron-left" size={16} />
                    </button>
                    <div className="users-actions-panel-titles">
                      <span className="users-actions-panel-title">{activeAction.label}</span>
                      <span className="users-actions-panel-subtitle">{t('users.actions.audienceTitle')}</span>
                    </div>
                  </div>

                  <div className="users-actions-audiences">
                    {audiences.map((aud) => {
                      const busy = busyAudience === aud.key;
                      return (
                        <button
                          key={aud.key}
                          type="button"
                          role="menuitem"
                          className={`users-actions-audience${aud.accent ? ' is-accent' : ''}${busy ? ' is-busy' : ''}`}
                          onClick={() => handleSelectAudience(aud.key)}
                          disabled={Boolean(busyAudience)}
                        >
                          <span className="users-actions-audience-icon">
                            {busy ? <span className="users-actions-spinner" aria-hidden /> : <Icon name={aud.icon} size={16} />}
                          </span>
                          <span className="users-actions-item-text">
                            <span className="users-actions-item-label">{aud.label}</span>
                            <span className="users-actions-item-hint">{aud.hint}</span>
                          </span>
                          <span className="users-actions-count">{aud.count}</span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UsersActionsMenu;
